import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { LegacyButton } from '@/components/LegacyButton';
import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { usePosition } from '@/hooks/usePosition';
import { useRouter } from '@/hooks/useRouter';
import type {
  BoutiqueSearchResult,
  StoreHit,
} from '@/integrations/algolia/types';
import {
  getCountryNameFromCode,
  getGoogleMapAddress,
  getTimeWithOffset,
  isWithinOpenHours,
  transformToFormat,
  updateHoursWithHolidaySchedule,
} from '@/lib/boutique/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getGeoDistance } from '@/lib/maps';
import { paths } from '@/lib/paths';
import { messages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import { BoutiqueService } from '../BoutiqueService/BoutiqueService';

import { ReactComponent as OtherDirectionSvg } from '@/assets/icons/icon_other_direction.svg';

import * as S from './BoutiqueDetails.styles';
interface BoutiqueDetailsProps {
  boutiqueHit: StoreHit;
}
type hourType = {
  time: string;
  val: string;
};
type currentPositionType = {
  lat: number;
  lng: number;
};
export const BoutiqueDetails = ({ boutiqueHit }: BoutiqueDetailsProps) => {
  const { formatMessage } = useIntl();
  const { sendEvent } = useGoogleAnalytics();
  const [distance, setDistance] = useState<number>();
  const { locale } = useRegionProvider();
  const langSlug = locale.slug;
  const streetText = () =>
    boutiqueHit ? getTranslatedStoreValue(boutiqueHit, 'street', langSlug) : '';
  const cityText = () =>
    boutiqueHit ? getTranslatedStoreValue(boutiqueHit, 'city', langSlug) : '';
  const [isOpenColor, setIsOpenColor] = useState<string>(theme.colors.error);
  const { push } = useRouter();
  const [openText, setOpenText] = useState<string>('');
  const { position } = usePosition();
  const currentPosition = {
    lat: position?.coords?.latitude,
    lng: position?.coords?.longitude,
  };
  const [openHours, setOpenHours] = useState<BoutiqueSearchResult['hours']>();
  const [reopenDate, setReopenDate] = useState<string | null>(null);

  useEffect(() => {
    if (boutiqueHit?.temporary_hours?.reopen_date) {
      setReopenDate(boutiqueHit.temporary_hours.reopen_date);
      setOpenText(formatMessage(messages.temporaryClosedUntil));
      setIsOpenColor(theme.colors.secondaryGrey);
      setOpenHours(undefined);
    } else if (boutiqueHit?.hours) {
      const formattedHours = transformToFormat(boutiqueHit?.hours);

      const updatedHours = updateHoursWithHolidaySchedule(
        formattedHours,
        boutiqueHit,
      );
      setOpenHours(updatedHours);
      const localTimeInMinutes = getTimeWithOffset(boutiqueHit?.utc_offset);
      const withinOrNot = isWithinOpenHours(localTimeInMinutes, formattedHours);
      if (withinOrNot) {
        setOpenText(formatMessage(messages.openNow));
        setIsOpenColor(theme.colors.primary);
      } else {
        setOpenText(formatMessage(messages.closed));
        setIsOpenColor(theme.colors.error);
      }
    } else {
      setOpenText(formatMessage(messages.temporaryClosed));
      setIsOpenColor(theme.colors.secondaryGrey);
      setOpenHours(undefined);
    }
  }, [
    boutiqueHit?.hours,
    boutiqueHit?.temporary_hours?.reopen_date,
    boutiqueHit?.utc_offset,
  ]);

  useEffect(() => {
    if (
      boutiqueHit?._geoloc?.lat &&
      position?.coords?.latitude &&
      position?.coords?.longitude
    ) {
      const geoDistance = getGeoDistance(
        currentPosition as currentPositionType,
        boutiqueHit?._geoloc,
      );
      setDistance(geoDistance);
    }
  }, [boutiqueHit?._geoloc?.lat, position?.coords?.latitude]);
  const offers_appointment = boutiqueHit?.offers_appointment ?? false;
  const offersBoutiquePickUp = boutiqueHit?.offers_boutique_pickup ?? false;
  const offersServiceYourWatch = boutiqueHit?.service_your_watch ?? false;
  const handleClickAppointment = () => {
    if (boutiqueHit?.country_code && boutiqueHit?.slug) {
      sendEvent({
        event: EVENT_NAME.INTENTION_BOOK_APPT,
      });

      push(
        paths.stores.appointment.asPath({
          country: boutiqueHit.country_code?.toLowerCase(),
          slug: boutiqueHit.slug,
        }),
      );
    }
  };

  return (
    <>
      <S.DetailContainer>
        <S.DetailContent>
          <S.InfoContainer>
            <S.AddressContainer>
              <S.InfoTitleWrapper>
                <S.InfoTitle>{formatMessage(messages.address)}</S.InfoTitle>
              </S.InfoTitleWrapper>
              <S.ItemsWrapper>
                <S.AddressLineContent>
                  {streetText()?.toLowerCase()}
                </S.AddressLineContent>
                <S.AddressLineContent>
                  {boutiqueHit?.postal_code &&
                    `${boutiqueHit.postal_code.toLowerCase()} `}
                  {cityText()?.toLowerCase()}
                </S.AddressLineContent>
                <S.AddressCountryContent>
                  {getCountryNameFromCode(boutiqueHit?.country_code)}
                </S.AddressCountryContent>
              </S.ItemsWrapper>
              {distance && (
                <S.DistanceWrapper>
                  <OtherDirectionSvg height={16} width={16} />
                  <S.Distance data-testid='distance'>
                    {distance + ' km'}
                  </S.Distance>
                </S.DistanceWrapper>
              )}
              <S.GetDirectionCta>
                <S.Button
                  colorVariant='blue'
                  href={getGoogleMapAddress(boutiqueHit?._geoloc)}
                  isUpperCase={false}
                  label={formatMessage(messages.getDirections)}
                  lower
                  onClick={() =>
                    sendEvent({
                      event: EVENT_NAME.LOCATOR_PIN_DETAILS,
                      retailer_name: boutiqueHit?.name,
                    })
                  }
                  target='_blank'
                  underline
                  variant='link'
                >
                  <S.Chevron height={24} />
                </S.Button>
              </S.GetDirectionCta>
            </S.AddressContainer>
            <S.ContactContainer>
              <S.InfoTitleWrapper>
                <S.InfoTitle>{formatMessage(messages.contacts)}</S.InfoTitle>
              </S.InfoTitleWrapper>
              <S.ItemsWrapper>
                <S.ContactItem>
                  <S.ContactItemName>
                    {formatMessage(messages.Phone)}
                  </S.ContactItemName>
                  <S.ContactItemContent>
                    <S.StyledLink
                      href={`tel:${boutiqueHit?.phone}`}
                      rel='noreferrer'
                    >
                      {boutiqueHit?.phone}
                    </S.StyledLink>
                  </S.ContactItemContent>
                </S.ContactItem>
                {boutiqueHit?.fax && (
                  <S.ContactItem>
                    <S.ContactItemName>
                      {formatMessage(messages.fax)}
                    </S.ContactItemName>
                    <S.ContactItemContent>
                      <S.StyledLink href='#' rel='noreferrer'>
                        {boutiqueHit?.fax}
                      </S.StyledLink>
                    </S.ContactItemContent>
                  </S.ContactItem>
                )}
                <S.ContactItem>
                  <S.ContactItemName>
                    {formatMessage(messages.email)}
                  </S.ContactItemName>
                  <S.ContactItemContent>
                    <S.StyledLink
                      href={`mailto:${boutiqueHit?.emails?.[0]}`}
                      rel='noreferrer'
                    >
                      {formatMessage(messages.sendAnEmail)}
                    </S.StyledLink>
                  </S.ContactItemContent>
                </S.ContactItem>
              </S.ItemsWrapper>
            </S.ContactContainer>
            <S.OpeningHoursContainer>
              <S.InfoTitleWrapper>
                <S.InfoTitle>{formatMessage(messages.openHours)}</S.InfoTitle>
              </S.InfoTitleWrapper>
              <S.ItemsWrapper>
                {openHours?.map((ele: hourType) => (
                  <S.ContactItem key={ele.val}>
                    <S.WeekdayName>
                      {formatMessage(
                        formMessages[
                          ele.val.toLowerCase() as keyof typeof formMessages
                        ] ?? ele.val,
                      )}
                    </S.WeekdayName>
                    <S.WeekDayHours>
                      {ele.time ?? formatMessage(messages.closed)}
                    </S.WeekDayHours>
                  </S.ContactItem>
                ))}
              </S.ItemsWrapper>
              <S.OpenTextWrapper>
                <S.StyledSpan color={isOpenColor} />
                {openText}
                {openText === formatMessage(messages.temporaryClosedUntil) &&
                  reopenDate && <span>{` ${reopenDate}`}</span>}
              </S.OpenTextWrapper>
            </S.OpeningHoursContainer>
          </S.InfoContainer>
          <S.CtaContainer>
            {offers_appointment && (
              <S.ButtonWrapper>
                <LegacyButton
                  colorVariant='primary'
                  data-test-id='AppointmentButton'
                  isFluid
                  label={formatMessage(commonMessages.bookAppointment)}
                  onClick={handleClickAppointment}
                  variant='filled'
                />
              </S.ButtonWrapper>
            )}
          </S.CtaContainer>
        </S.DetailContent>
      </S.DetailContainer>

      <BoutiqueService
        offersBoutiquePickUp={offersBoutiquePickUp}
        offersServiceYourWatch={offersServiceYourWatch}
      />
    </>
  );
};
